/**
 * Branding Models
 * 
 * Simplified type definitions matching the backend BrandingConfig.
 * 
 * Backend structure:
 * {
 *   "Colors": { "Header": "#...", "PageBackground": "#...", "Font": "#...", "Button": "#..." },
 *   "Assets": { "Logo": "url", "Favicon": "url", "EmbroideryLogo": "url" }
 * }
 */

/**
 * Simplified branding colors - only 4 essential colors
 */
export interface BrandingColors {
    /** Header/navbar background color (hex) */
    header: string;

    /** Page background color (hex) */
    pageBackground: string;

    /** Primary font/text color (hex) */
    font: string;

    /** Button color (hex) */
    button: string;
}

/**
 * Branding assets (logos, favicon)
 */
export interface BrandingAssets {
    /** Main logo URL */
    logo: string;

    /** Favicon URL */
    favicon: string;

    /** Embroidery logo URL (optional) */
    embroideryLogo: string;
}

/**
 * Complete branding configuration
 * This is the frontend model that maps to/from the backend DTO
 */
export interface BrandingConfig {
    /** Color configuration */
    colors: BrandingColors;

    /** Asset URLs */
    assets: BrandingAssets;

    /** Organization ID (set locally, not from API) */
    organizationId?: string;

    /** Organization name for page title */
    organizationName?: string;
}

/**
 * API DTO format (PascalCase from C# backend)
 */
export interface BrandingConfigDto {
    Colors: {
        Header: string;
        PageBackground: string;
        Font: string;
        Button: string;
    };
    Assets: {
        Logo: string;
        Favicon: string;
        EmbroideryLogo: string;
    };
    OrganizationName?: string;
}

/**
 * Get default branding configuration
 */
export function getDefaultBrandingConfig(): BrandingConfig {
    return {
        colors: {
            header: '#BFA05A',
            pageBackground: '#1A1A1A',
            font: '#ffffff',
            button: '#BFA05A'
        },
        assets: {
            logo: '',
            favicon: '/favicon.ico',
            embroideryLogo: ''
        },
        organizationName: 'My Site'
    };
}

/**
 * Convert API DTO (PascalCase) to frontend model (camelCase)
 * Handles both PascalCase (C# backend) and camelCase (JSON) formats
 */
export function fromDto(dto: any): BrandingConfig {
    // Handle both PascalCase and camelCase
    const colors = dto?.Colors || dto?.colors || {};
    const assets = dto?.Assets || dto?.assets || {};
    
    const config: BrandingConfig = {
        colors: {
            header: colors?.Header || colors?.header || '#BFA05A',
            pageBackground: colors?.PageBackground || colors?.pageBackground || '#1A1A1A',
            font: colors?.Font || colors?.font || '#ffffff',
            button: colors?.Button || colors?.button || '#BFA05A'
        },
        assets: {
            logo: assets?.Logo || assets?.logo || '',
            favicon: assets?.Favicon || assets?.favicon || '/favicon.ico',
            embroideryLogo: assets?.EmbroideryLogo || assets?.embroideryLogo || ''
        },
        organizationName: dto?.OrganizationName || dto?.organizationName || 'My Site'
    };
    
    console.log('[BrandingModels] fromDto input:', dto);
    console.log('[BrandingModels] fromDto output:', config);
    
    return config;
}

/**
 * Convert frontend model (camelCase) to API DTO (PascalCase)
 */
export function toDto(config: BrandingConfig): BrandingConfigDto {
    return {
        Colors: {
            Header: config.colors.header,
            PageBackground: config.colors.pageBackground,
            Font: config.colors.font,
            Button: config.colors.button
        },
        Assets: {
            Logo: config.assets.logo || '',
            Favicon: config.assets.favicon || '/favicon.ico',
            EmbroideryLogo: config.assets.embroideryLogo || ''
        },
        OrganizationName: config.organizationName || 'My Site'
    };
}

/**
 * Check if a color is a valid hex color
 */
export function isValidHexColor(color: string): boolean {
    return /^#[0-9A-Fa-f]{3,8}$/.test(color);
}
